import { Dispatch, DispatchWithoutAction } from "react";
import { Field, Form } from "react-final-form";
import { CheckIcon } from "lucide-react";

import { useTranslation } from "@/lib/i18n";
import type { StaticMapOverlaysOverlayBase } from "@/lib/api";
import Dialog from "@/components/dialog/dialog";
import DialogHeader from "@/components/dialog/dailog-header";
import dialogVariants from "@/components/dialog/dialog-variants.module.css";
import { TextField } from "@/features/shared/components";
import Button from "@/components/button/button";
import Stack from "@/components/stack/stack";
import DialogFooter from "@/components/dialog/dialog-footer";
import { ItemCardContent } from "@/features/widget-forms/forms/weather-model-map/components/item-card-content/item-card-content";
import { StaticOverlayColorSelect } from "@/features/widget-forms/forms/weather-model-map/components/static-overlay-color-select";
import { DEFAULT_STATIC_OVERLAY_COLOR } from "@/features/shared/widgets/model-map/static-overlay-color-presets";
import styles from "@/features/widget-forms/forms/map-widget-editor/configure-layer-modal.module.css";

export interface StaticOverlayDetails {
  label: string;
  color: string;
}

interface ConfigureStaticOverlayModalProps {
  overlay: StaticMapOverlaysOverlayBase | undefined;
  open: boolean;
  onSubmit: Dispatch<StaticOverlayDetails>;
  onCancel: DispatchWithoutAction;
  initialValues?: Partial<StaticOverlayDetails>;
}

export function ConfigureStaticOverlayModal({
  open,
  overlay,
  onSubmit,
  onCancel,
  initialValues,
}: ConfigureStaticOverlayModalProps) {
  const { t } = useTranslation();
  return (
    <Dialog open={open}>
      <Dialog.Content className={dialogVariants.default}>
        <Form onSubmit={onSubmit}>
          {(formProps) => (
            <form onSubmit={formProps.handleSubmit}>
              <DialogHeader.Root>
                <DialogHeader.Title>
                  {t("admin.widgets.fields.model_map.static_overlays.configure_title")}
                </DialogHeader.Title>
              </DialogHeader.Root>
              <h3 className={styles.header}>
                {t("admin.widgets.fields.model_map.static_overlays.preview_header")}
              </h3>
              {overlay ? (
                <div className={styles.layerPreview}>
                  <ItemCardContent title={overlay.name} />
                </div>
              ) : null}
              <h3 className={styles.header}>
                {t("admin.widgets.fields.model_map.static_overlays.settings_header")}
              </h3>
              <TextField
                name="label"
                label={t("admin.widgets.fields.model_map.static_overlays.label_field")}
                helperText={t(
                  "admin.widgets.fields.model_map.static_overlays.label_helper",
                )}
                autoFocus
                required
                defaultValue={initialValues?.label}
                className={styles.textField}
              />
              <Field
                name="color"
                initialValue={initialValues?.color ?? DEFAULT_STATIC_OVERLAY_COLOR}
              >
                {({ input }) => (
                  <div className={styles.textField}>
                    <StaticOverlayColorSelect
                      value={input.value ?? ""}
                      onValueChange={input.onChange}
                      label={t(
                        "admin.widgets.fields.model_map.static_overlays.color_field",
                      )}
                      showUnknownHint={false}
                    />
                  </div>
                )}
              </Field>
              <DialogFooter>
                <Stack flexDirection="row" justifyContent={"flex-end"} gap={8}>
                  <Button type="button" variant="solid-secondary" onClick={onCancel}>
                    {t("general.cancel")}
                  </Button>
                  <Button type="submit" variant="solid">
                    <CheckIcon />
                    {t("general.done")}
                  </Button>
                </Stack>
              </DialogFooter>
            </form>
          )}
        </Form>
      </Dialog.Content>
    </Dialog>
  );
}
